import { Component, Input, ViewChildren, QueryList, ElementRef, Renderer2, Output, EventEmitter, TemplateRef, ChangeDetectionStrategy } from '@angular/core';

import { UtilsService } from '@common/services';

import { TreeModel } from './tree-model';

/**
 item {
    children: item[]
    data: {
        id
        label
        parentId
        entry: la donéne de base
    }
    depth: profondeur du noeud
    height: nombre de noeuds enfant
    id: string
    label: string (traduit)
    parent: item
 }
 */
@Component({
    selector: 'aa-treeview',
    templateUrl: './treeview.component.html',
    styleUrls: [
        './treeview.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TreeviewComponent
{
    private _treeModel: TreeModel = null;
    @Input()
    get treeModel (): TreeModel
    {
        return this._treeModel;
    }
    set treeModel (value: TreeModel)
    {
        this._treeModel = value;
        if (this._treeModel)
        {
            this.isLoaded = true;
            Promise.resolve(null).then(() =>
            {
                this.createInitMap();
                this.eleList = this.options.toArray();
                this.setInitialActiveDescendant();
            });
        }
    }
    @Input() hoverLeaves: boolean = true;
    @ViewChildren('options') options: QueryList<ElementRef>;
    @ViewChildren('icons') iconsTags: QueryList<ElementRef>;
    @Output() itemSelected: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemActivated: EventEmitter<any> = new EventEmitter<any>();
    @Output() itemDeactivated: EventEmitter<any> = new EventEmitter<any>();
    eleList: ElementRef[];
    eleMap: Map<string, ElementRef>;
    iconsMap: Map<string, ElementRef>;
    TREEVIEW_SUFFIX: string = '_treeView';
    ICON_SUFFIX: string = '_icon';
    ARIA_EXPANDED: string = 'aria-expanded';
    DATA_CHILDCOUNT = 'data-childcount';
    DATA_LID: string = 'data-lid';
    DATA_CODE: string = 'data-code';
    DATA_ISFIRST: string = 'data-first';
    DATA_ISLAST: string = 'data-last';
    TAB_INDEX: string = 'tabindex';
    currentActiveDescendantElement: string;
    public isLoaded: boolean = false;

    constructor (private renderer: Renderer2)
    {
    }

    setInitialActiveDescendant (): void
    {
        if (!UtilsService.isNullOrUndefined(this.eleList) && Array.isArray(this.eleList))
        {
            this.currentActiveDescendantElement = this.eleList[0]?.nativeElement.id;
        }
    }

    createInitMap (): void
    {
        this.eleMap = new Map<string, ElementRef>();
        this.options.forEach((eleRef: ElementRef, index: number): void =>
        {
            this.renderer.setAttribute(eleRef.nativeElement, this.DATA_LID, index.toString());
            this.eleMap.set(eleRef.nativeElement.id, eleRef);
        });

        this.iconsMap = new Map<string, ElementRef>();
        this.iconsTags.forEach((eleRef: ElementRef): void =>
        {
            this.iconsMap.set(eleRef.nativeElement.id, eleRef);
        });
    }

    setTabIndexOnLoad (role: string, i: number)
    {
        return (role == 'tree' && i == 0) ? '0' : '-1';
    }

    toggleNode (item: any): void
    {
        this.options.some((eleRef: ElementRef): boolean =>
        {
            const code = eleRef.nativeElement.getAttribute('data-code');

            if (code == item[this.treeModel.cid])
            {
                if (this.isLeaf(item))
                {
                    this.selectItem(item);

                    return false;
                }

                const isExpanded = eleRef.nativeElement.getAttribute(this.ARIA_EXPANDED);

                this.setElementAttribute(eleRef, this.ARIA_EXPANDED, (isExpanded === 'true') ? 'false' : 'true');
                if (this.currentActiveDescendantElement != null)
                {
                    const caliEleRef = this.eleMap.get(this.currentActiveDescendantElement);

                    if (caliEleRef != null)
                    {
                        this.setElementAttribute(caliEleRef, this.TAB_INDEX, '-1');
                        this.setElementAttribute(eleRef, this.TAB_INDEX, '0');
                        this.currentActiveDescendantElement = eleRef.nativeElement.id;
                    }
                }

                return true;
            }
            else
            {
                return false;
            }
        });
    }

    onMouseenter (item: any): void
    {
        this.itemActivated.emit(item);
    }

    onMouseleave (item: any): void
    {
        this.itemDeactivated.emit(item);
    }

    selectItem (item: any): void
    {
        this.itemSelected.emit(item.data.entry);
    }

    getExpandCollapseClass (item: any): string
    {
        if (item != null && item[this.treeModel.cnid] != null && item[this.treeModel.cnid].length > 0)
        {
            return 'fal fa-fw ' + this.treeModel.iconExpand;
        }

        return 'fal fa-fw ' + this.treeModel.iconLeaf;
    }

    getLeafClass (item: any): string
    {
        if (item != null && this.treeModel.iconLeaf !== '')
        {
            return 'fal fa-fw ' + this.treeModel.iconLeaf;
        }

        return null;
    }

    isChildrenExists (items: any[]): boolean
    {
        return (items != null && items.length > 0) ? true : false;
    }

    collapsedState (item: any): string
    {
        if (item != null && item[this.treeModel.cnid] != null && item[this.treeModel.cnid].length > 0)
        {
            item.ariaExpanded = false;

            return 'false';
        }

        item.ariaExpanded = null;

        return null;

    }

    isLeaf (item: any): boolean
    {
        return this.getChildCount(item) === 0;
    }

    hasTemplate = (item: any): boolean =>
    {
        const depth: number = item['depth'];

        return this.treeModel.templates.length >= depth && !UtilsService.isNullOrUndefined(this.treeModel.templates[depth]);
    };

    getTemplate (item: any): TemplateRef<any>
    {
        const depth: number = item['depth'];

        return this.treeModel.templates[depth];
    }

    executeKeydown ($event): void
    {
        const keyCode = $event.which;

        switch (keyCode)
        {
            case 40://Down Arrow
                this.executeArrowDown($event);
                break;

            case 38:// Up Arrow
                this.executeUpArrow($event);
                break;
            case 37:// RightArrow
                this.executeLeftArrow($event);
                break;

            case 39:// RightArrow
                this.executeRightArrow($event);
                break;
            case 32:// Spacebar
                this.selectOption($event);
                break;
        }
    }

    // toggleExpand (eleRef: ElementRef, state: string)
    // {
    //     this.setElementAttribute(eleRef, this.ARIA_EXPANDED, state);
    // }

    isExpanded (eleRef: ElementRef): boolean
    {
        const isExpanded = eleRef.nativeElement.getAttribute(this.ARIA_EXPANDED);
        const childCount = eleRef.nativeElement.getAttribute(this.DATA_CHILDCOUNT);

        if (isExpanded == 'true' && !isNaN(childCount) && parseInt(childCount) > 0)
        {
            return true;
        }

        return false;
    }

    getChildCount (item: any): number
    {
        if (item != null && item[this.treeModel.cnid] != null)
        {
            const items = item[this.treeModel.cnid];

            return (Array.isArray(items) ? items.length : 0);
        }

        return 0;
    }

    setElementAttribute (eleRef: ElementRef, attribute: string, value: string): void
    {
        this.renderer.setAttribute(eleRef.nativeElement, attribute, value);

        if (attribute === this.ARIA_EXPANDED)
        {
            const icon = eleRef.nativeElement.querySelector('i');
            const iconEleRef = this.iconsMap.get(icon.id);

            if (value == 'true')
            {
                this.renderer.removeClass(iconEleRef.nativeElement, this.treeModel.iconExpand);
                this.renderer.addClass(iconEleRef.nativeElement, this.treeModel.iconCollapse);
            }
            else if (value == 'false')
            {
                this.renderer.removeClass(iconEleRef.nativeElement, this.treeModel.iconCollapse);
                this.renderer.addClass(iconEleRef.nativeElement, this.treeModel.iconExpand);
            }
        }
    }
    executeRightArrow ($event): void
    {
        $event.stopImmediatePropagation();
        $event.preventDefault();

        const eleRef: ElementRef = this.eleMap.get($event.currentTarget.id);
        const childCount = eleRef.nativeElement.getAttribute(this.DATA_CHILDCOUNT);

        if (!isNaN(childCount) && parseInt(childCount) > 0)
        {
            const isExpanded = eleRef.nativeElement.getAttribute(this.ARIA_EXPANDED);

            if (isExpanded == 'false')
                this.setElementAttribute(eleRef, this.ARIA_EXPANDED, 'true');

        }

    }

    executeLeftArrow ($event): void
    {
        $event.stopImmediatePropagation();
        $event.preventDefault();

        const eleRef: ElementRef = this.eleMap.get($event.currentTarget.id);
        const childCount = eleRef.nativeElement.getAttribute(this.DATA_CHILDCOUNT);

        if (!isNaN(childCount) && parseInt(childCount) > 0)
        {
            const isExpanded = eleRef.nativeElement.getAttribute(this.ARIA_EXPANDED);

            if (isExpanded == 'true')
                this.setElementAttribute(eleRef, this.ARIA_EXPANDED, 'false');

        }

    }

    executeArrowDown ($event): void
    {
        $event.stopImmediatePropagation();
        $event.preventDefault();

        const eleRef: ElementRef = this.eleMap.get($event.currentTarget.id);

        this.recursiveDownNavigation(eleRef, 0);
    }

    executeUpArrow ($event): void
    {
        $event.stopImmediatePropagation();
        $event.preventDefault();

        const eleRef: ElementRef = this.eleMap.get($event.currentTarget.id);

        this.recursiveUpNavigation(eleRef, 0);
    }

    recursiveUpNavigation (eleRef: ElementRef<any>, loopCounter: number): void
    {
        const lid = eleRef.nativeElement.getAttribute(this.DATA_LID);
        const isFirst = eleRef.nativeElement.getAttribute(this.DATA_ISFIRST);

        if (isFirst == '0' && loopCounter > 0)
        {
            if (this.isExpanded(eleRef))
            {
                const ul = eleRef.nativeElement.querySelector('ul');
                const lastChild = ul.lastChild;
                const childEleRef = this.eleMap.get(lastChild.id);

                if (this.isExpanded(childEleRef))
                {
                    this.recursiveUpNavigation(childEleRef, loopCounter + 1);
                }
                else
                {
                    this.setElementAttribute(childEleRef, this.TAB_INDEX, '-1');
                    this.setElementAttribute(childEleRef, this.TAB_INDEX, '0');
                    this.setToFocus(childEleRef);
                }
            }
            else
            {
                this.setElementAttribute(eleRef, this.TAB_INDEX, '0');
                this.setToFocus(eleRef);
            }
        }

        if (isFirst == '0' && loopCounter == 0)
        {
            const prevLid = parseInt(lid) - 1;

            if (prevLid > -1)
            {
                const preVEleRef: ElementRef = this.eleList[prevLid];

                this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                if (this.isExpanded(preVEleRef))
                {
                    const ul = preVEleRef.nativeElement.querySelector('ul');
                    const lastChild = ul.lastChild;
                    const childEleRef = this.eleMap.get(lastChild.id);

                    if (this.isExpanded(childEleRef))
                    {
                        this.recursiveUpNavigation(childEleRef, loopCounter + 1);
                    }
                    else
                    {
                        this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                        this.setElementAttribute(childEleRef, this.TAB_INDEX, '0');
                        this.setToFocus(childEleRef);
                    }
                }
                else
                {
                    this.setElementAttribute(preVEleRef, this.TAB_INDEX, '0');
                    this.setToFocus(preVEleRef);
                }
            }
        }
        else if (isFirst == '1')
        {
            const parent = eleRef.nativeElement.parentElement;
            const role = parent.getAttribute('role');

            if (role == 'group')
            {
                const superParent = parent.parentElement;
                const role = superParent.getAttribute('role');

                if (role == 'treeitem')
                {
                    const subeleRef: ElementRef = this.eleMap.get(superParent.id);

                    this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                    this.setElementAttribute(subeleRef, this.TAB_INDEX, '0');
                    this.setToFocus(subeleRef);
                    if (!this.isExpanded(subeleRef))
                    {
                        this.setElementAttribute(subeleRef, this.TAB_INDEX, '0');
                        this.setToFocus(subeleRef);
                    }
                }
            }
        }
    }

    selectOption ($event): void
    {
        // console.debug({ evt: $event });
    }

    getTreeItemId (item: any): any
    {
        return item[this.treeModel.cid];
    }

    getIconId (item: any): string
    {
        const itemId = this.getTreeItemId(item);

        return itemId + '_' + this.ICON_SUFFIX;
    }

    setToFocus (eleRef: ElementRef): void
    {
        this.currentActiveDescendantElement = eleRef.nativeElement.id;
        eleRef.nativeElement.focus();
        this.scrollIntoViewSmoothly(eleRef);
    }

    scrollIntoViewSmoothly (eleRef: ElementRef): void
    {
        eleRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    recursiveDownNavigation (eleRef: ElementRef, loopCounter: number): void
    {
        const lid = eleRef.nativeElement.getAttribute(this.DATA_LID);
        const isLast = eleRef.nativeElement.getAttribute(this.DATA_ISLAST);

        if (isLast == '0')
        {
            if (this.isExpanded(eleRef) && loopCounter < 1)
            {
                const li = eleRef.nativeElement.querySelector('li');

                if (li != null)
                {
                    const nextEleRef: ElementRef = this.eleMap.get(li.id);

                    this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                    this.setElementAttribute(nextEleRef, this.TAB_INDEX, '0');
                    this.setToFocus(nextEleRef);
                }
            }
            else
            {
                const nextLid = parseInt(lid) + 1;
                if (nextLid <= this.options.length)
                {
                    const nextEleRef: ElementRef = this.eleList[nextLid];

                    this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                    this.setElementAttribute(nextEleRef, this.TAB_INDEX, '0');
                    this.setToFocus(nextEleRef);
                }
            }
        }
        else if (isLast == '1')
        {
            if (this.isExpanded(eleRef) && loopCounter < 1)
            {
                const li = eleRef.nativeElement.querySelector('li');

                if (li != null)
                {
                    const nextEleRef: ElementRef = this.eleMap.get(li.id);

                    this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                    this.setElementAttribute(nextEleRef, this.TAB_INDEX, '0');
                    this.setToFocus(nextEleRef);
                }
            }
            else
            {
                const parent = eleRef.nativeElement.parentElement;
                const role = parent.getAttribute('role');

                if (role == 'group')
                {
                    const superParent = parent.parentElement;
                    const role = superParent.getAttribute('role');

                    if (role == 'treeitem')
                    {
                        const subeleRef: ElementRef = this.eleMap.get(superParent.id);

                        this.setElementAttribute(eleRef, this.TAB_INDEX, '-1');
                        this.recursiveDownNavigation(subeleRef, loopCounter + 1);
                    }
                }
            }
        }
    }
}
